import React from 'react';
import {
  Box,
  Button,
  Chip,
  Stack,
  Tooltip,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSidebarContentLeft } from '../hooks/useSidebarContentLeft';

export interface FormHotkeyBarItem {
  id: string;
  /** Etiqueta, p. ej. "F4-Dtos/Inc x Linea" o "F2-Aceptar" */
  label: string;
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'accept' | 'cancel';
}

interface FormHotkeyBarProps {
  items: FormHotkeyBarItem[];
  /** Si true, queda fija al pie del viewport. */
  sticky?: boolean;
  /** Texto opcional a la derecha (p. ej. nro de formulario). */
  rightMeta?: string | number | null;
  /** Contenido a la izquierda de los botones de función (p. ej. «Atajos»). */
  leadingSlot?: React.ReactNode;
}

/** Separa "F4-Dtos/Inc" → { key: "F4", rest: "Dtos/Inc" } */
export function splitHotkeyLabel(label: string): { key: string; rest: string } {
  const match = label.match(/^([A-Za-z0-9]+)\s*[-–—]\s*(.+)$/);
  if (match) {
    return { key: match[1], rest: match[2] };
  }
  return { key: label, rest: '' };
}

function isActionVariant(variant?: FormHotkeyBarItem['variant']): boolean {
  return variant === 'accept' || variant === 'cancel';
}

const KeyBadge: React.FC<{ label: string; dense?: boolean }> = ({ label, dense }) => {
  const theme = useTheme();
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: dense ? 18 : 22,
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        color: 'primary.main',
        fontWeight: 700,
        fontSize: dense ? '0.62rem' : '0.7rem',
        borderRadius: 1,
        '& .MuiChip-label': { px: dense ? 0.5 : 0.75 },
      }}
    />
  );
};

const btnBase = {
  textTransform: 'none' as const,
  fontWeight: 500,
  borderRadius: 2,
};

/**
 * Barra de atajos eOne (desktop).
 * En mobile no se renderiza: las acciones van en el panel de totales / botones del formulario.
 */
const FormHotkeyBar: React.FC<FormHotkeyBarProps> = ({
  items,
  sticky = true,
  rightMeta: _rightMeta,
  leadingSlot,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sidebarLeft = useSidebarContentLeft();
  const primary = theme.palette.primary.main;

  /** En mobile no se muestra la botonera (acciones van en el panel de totales / botones del form). */
  if (isMobile) {
    return null;
  }

  const functionItems = items.filter((i) => !isActionVariant(i.variant));
  const actionItems = [...items.filter((i) => isActionVariant(i.variant))].sort((a, b) => {
    if (a.variant === 'cancel') return -1;
    if (b.variant === 'cancel') return 1;
    return 0;
  });

  const functionButtons = () =>
    functionItems.map((item) => {
      const { key, rest } = splitHotkeyLabel(item.label);
      return (
        <Tooltip key={item.id} title={item.tooltip} arrow placement="top">
          <span style={{ minWidth: 0 }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={item.disabled}
              onClick={item.onClick}
              sx={{
                ...btnBase,
                borderRadius: 2,
                fontSize: '0.8125rem',
                whiteSpace: 'nowrap',
                px: 1,
                py: 0.6,
                minHeight: 36,
                gap: 0.75,
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              <KeyBadge label={key} />
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {rest || item.label}
              </Box>
            </Button>
          </span>
        </Tooltip>
      );
    });

  const actionButtons = () =>
    actionItems.map((item) => {
      const isAccept = item.variant === 'accept';
      return (
        <Button
          key={item.id}
          size="small"
          variant={isAccept ? 'contained' : 'outlined'}
          color="primary"
          disabled={item.disabled}
          onClick={item.onClick}
          sx={{
            ...btnBase,
            whiteSpace: 'nowrap',
            minHeight: 44,
            px: 2.5,
            fontSize: '0.875rem',
            flexShrink: 0,
          }}
        >
          {item.label}
        </Button>
      );
    });

  return (
    <Box
      role="toolbar"
      aria-label="Atajos de teclado del formulario"
      data-form-hotkey-bar="true"
      sx={
        sticky
          ? {
              position: 'fixed',
              left: { xs: 0, md: sidebarLeft },
              right: 0,
              bottom: 0,
              zIndex: (t) => t.zIndex.appBar - 1,
              px: { xs: 1, sm: 1.5, md: 2, lg: 3 },
              pb: {
                xs: 'max(8px, env(safe-area-inset-bottom))',
                md: 1.5,
              },
              pt: { xs: 0.5, md: 0 },
              transition: 'left 0.3s ease-in-out',
            }
          : { width: '100%', mt: 2 }
      }
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          px: { xs: 1.25, sm: 1.5, md: 1.75 },
          py: { xs: 1, md: 1.1 },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: { xs: '16px 16px 0 0', md: 2 },
          boxShadow: sticky
            ? '0 -4px 24px rgba(15, 23, 42, 0.1)'
            : '0 2px 10px rgba(15, 23, 42, 0.06)',
          boxSizing: 'border-box',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 4 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(primary, 0.25),
                borderRadius: 2,
              },
            }}
          >
            {leadingSlot ? (
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{leadingSlot}</Box>
            ) : null}
            {functionButtons()}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            {actionButtons()}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default FormHotkeyBar;
